import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  Check,
  X,
  Edit2,
  Trash2,
  Umbrella,
  Calendar,
  Sun,
  Users
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Vacations = () => {
  const { isAdmin, isSupervisor, isWorker, user } = useAuth();
  const { t, language } = useLanguage();
  const [vacations, setVacations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [myEmployee, setMyEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedEmployeeForBalance, setSelectedEmployeeForBalance] = useState('');
  const [vacationBalance, setVacationBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  
  // Form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVacation, setEditingVacation] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    start_date: '',
    end_date: '',
    days: 0,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  useEffect(() => {
    // Fetch balance when employee is selected or for worker's own profile
    if (selectedEmployeeForBalance) {
      fetchVacationBalance(selectedEmployeeForBalance);
    } else if (myEmployee) {
      fetchVacationBalance(myEmployee.id);
    }
  }, [selectedEmployeeForBalance, myEmployee]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      
      if (isWorker()) {
        const myEmpRes = await axios.get(`${API_URL}/employees/me`);
        setMyEmployee(myEmpRes.data);
        params.employee_id = myEmpRes.data.id;
        
        const vacationsRes = await axios.get(`${API_URL}/vacations`, { params });
        setVacations(vacationsRes.data);
        setEmployees([myEmpRes.data]);
      } else {
        const [vacationsRes, employeesRes] = await Promise.all([
          axios.get(`${API_URL}/vacations`, { params }),
          axios.get(`${API_URL}/employees`)
        ]);
        setVacations(vacationsRes.data);
        setAllEmployees(employeesRes.data);
        setEmployees(employeesRes.data.filter(e => e.is_active !== false));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVacationBalance = async (employeeId) => {
    if (!employeeId) return;
    
    setLoadingBalance(true);
    try {
      const response = await axios.get(`${API_URL}/vacations/balance/${employeeId}`);
      setVacationBalance(response.data);
    } catch (error) {
      console.error('Error fetching vacation balance:', error);
      setVacationBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let employeeId = formData.employee_id;
      
      if (isWorker() && myEmployee) {
        employeeId = myEmployee.id;
      }
      
      if (!employeeId && !editingVacation) {
        toast.error(language === 'es' ? 'Debe seleccionar un empleado' : 'You must select an employee');
        return;
      }
      
      if (editingVacation) {
        await axios.put(`${API_URL}/vacations/${editingVacation.id}`, {
          start_date: formData.start_date,
          end_date: formData.end_date,
          days: formData.days,
          notes: formData.notes
        });
        toast.success(language === 'es' ? 'Vacaciones actualizadas' : 'Vacation updated');
      } else {
        await axios.post(`${API_URL}/vacations`, {
          ...formData,
          employee_id: employeeId
        });
        toast.success(language === 'es' ? 'Solicitud creada' : 'Request created');
      }
      
      fetchData();
      // Refresh balance
      if (selectedEmployeeForBalance) {
        fetchVacationBalance(selectedEmployeeForBalance);
      } else if (myEmployee) {
        fetchVacationBalance(myEmployee.id);
      }
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleEdit = (vacation) => {
    setEditingVacation(vacation);
    setFormData({
      employee_id: vacation.employee_id,
      start_date: vacation.start_date,
      end_date: vacation.end_date,
      days: vacation.days,
      notes: vacation.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta solicitud?' : 'Delete this request?')) return;
    
    try {
      await axios.delete(`${API_URL}/vacations/${id}`);
      toast.success(language === 'es' ? 'Solicitud eliminada' : 'Request deleted');
      fetchData();
      // Refresh balance
      if (selectedEmployeeForBalance) {
        fetchVacationBalance(selectedEmployeeForBalance);
      } else if (myEmployee) {
        fetchVacationBalance(myEmployee.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingVacation(null);
    setFormData({ employee_id: '', start_date: '', end_date: '', days: 0, notes: '' });
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API_URL}/vacations/${id}/approve`);
      toast.success(language === 'es' ? 'Vacaciones aprobadas' : 'Vacation approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(`${API_URL}/vacations/${id}/reject`);
      toast.success(language === 'es' ? 'Vacaciones rechazadas' : 'Vacation rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const calculateDays = () => {
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      setFormData(prev => ({ ...prev, days: Math.max(0, diff) }));
    }
  };

  useEffect(() => {
    calculateDays();
  }, [formData.start_date, formData.end_date]);

  const getEmployeeName = (id) => allEmployees.find(e => e.id === id)?.name || employees.find(e => e.id === id)?.name || id;

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-emerald-100 text-emerald-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: t('pending'),
      approved: t('approved'),
      rejected: t('rejected')
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vacations-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{t('vacations')}</h1>
          <p className="text-slate-500">
            {language === 'es' ? 'Gestión de solicitudes de vacaciones' : 'Vacation requests management'}
          </p>
        </div>
        <Button 
          className="bg-indigo-600 hover:bg-indigo-700" 
          data-testid="request-vacation-btn"
          onClick={() => {
            setEditingVacation(null);
            setFormData({ employee_id: '', start_date: '', end_date: '', days: 0, notes: '' });
            setDialogOpen(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('request_vacation')}
        </Button>
      </div>

      {/* Vacation Balance Card */}
      <Card className="border-indigo-200 bg-indigo-50/50">
        <CardHeader className="pb-2">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="font-['Manrope'] text-lg flex items-center gap-2 text-indigo-900">
              <Calendar className="w-5 h-5" />
              {language === 'es' ? 'Balance de Vacaciones' : 'Vacation Balance'}
              {vacationBalance && <span className="text-sm font-normal">({vacationBalance.year})</span>}
            </CardTitle>
            {!isWorker() && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                <Select 
                  value={selectedEmployeeForBalance} 
                  onValueChange={setSelectedEmployeeForBalance}
                >
                  <SelectTrigger className="w-48 bg-white" data-testid="balance-employee-select">
                    <SelectValue placeholder={language === 'es' ? 'Seleccionar empleado' : 'Select employee'} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingBalance ? (
            <div className="flex items-center justify-center py-4">
              <div className="spinner" />
            </div>
          ) : vacationBalance ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-3 bg-white rounded-lg border border-indigo-100">
                <div className="text-xs text-slate-500 uppercase">{language === 'es' ? 'Días Base' : 'Base Days'}</div>
                <div className="text-2xl font-bold text-indigo-900">{vacationBalance.entitled_days}</div>
              </div>
              {vacationBalance.holidays_worked_days > 0 && (
                <div className="p-3 bg-white rounded-lg border border-amber-200">
                  <div className="text-xs text-slate-500 uppercase flex items-center gap-1">
                    <Sun className="w-3 h-3" />
                    {language === 'es' ? 'Festivos Trab.' : 'Holidays Worked'}
                  </div>
                  <div className="text-2xl font-bold text-amber-600">+{vacationBalance.holidays_worked_days}</div>
                </div>
              )}
              <div className="p-3 bg-white rounded-lg border border-emerald-200">
                <div className="text-xs text-slate-500 uppercase">{language === 'es' ? 'Total' : 'Total'}</div>
                <div className="text-2xl font-bold text-emerald-600">{vacationBalance.total_entitled_days}</div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-slate-200">
                <div className="text-xs text-slate-500 uppercase">{language === 'es' ? 'Usados' : 'Used'}</div>
                <div className="text-2xl font-bold text-slate-600">{vacationBalance.used_days}</div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-amber-200">
                <div className="text-xs text-slate-500 uppercase">{language === 'es' ? 'Pendientes' : 'Pending'}</div>
                <div className="text-2xl font-bold text-amber-600">{vacationBalance.pending_days}</div>
              </div>
              <div className="p-3 bg-indigo-100 rounded-lg border border-indigo-300 md:col-span-2">
                <div className="text-xs text-indigo-700 uppercase font-medium">
                  {language === 'es' ? 'Disponibles para solicitar' : 'Available to request'}
                </div>
                <div className="text-3xl font-bold text-indigo-900">{vacationBalance.available_days}</div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-4">
              {isWorker() 
                ? (language === 'es' ? 'No se pudo cargar el balance' : 'Could not load balance')
                : (language === 'es' ? 'Selecciona un empleado para ver su balance' : 'Select an employee to view their balance')
              }
            </p>
          )}
        </CardContent>
      </Card>

      {/* Vacation Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingVacation 
                ? (language === 'es' ? 'Editar Vacaciones' : 'Edit Vacation')
                : t('request_vacation')
              }
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isWorker() && !editingVacation && (
              <div className="space-y-2">
                <Label>{t('employees')}</Label>
                <Select 
                  value={formData.employee_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, employee_id: value }))}
                >
                  <SelectTrigger data-testid="vacation-employee-select">
                    <SelectValue placeholder={language === 'es' ? 'Seleccionar empleado' : 'Select employee'} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editingVacation && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">{language === 'es' ? 'Empleado' : 'Employee'}</p>
                <p className="font-medium">{getEmployeeName(editingVacation.employee_id)}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('start_date')}</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                  data-testid="vacation-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('end_date')}</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  required
                  data-testid="vacation-end-date"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <Umbrella className="w-5 h-5 text-indigo-600" />
              <span className="text-sm text-slate-600">
                {language === 'es' ? 'Total días' : 'Total days'}: <strong className="text-slate-900">{formData.days}</strong>
              </span>
            </div>
            <div className="space-y-2">
              <Label>{t('notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={language === 'es' ? 'Notas adicionales...' : 'Additional notes...'}
                data-testid="vacation-notes"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="vacation-submit-btn">
                {t('save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label>{t('status')}</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="vacation-status-filter">
                <SelectValue placeholder={language === 'es' ? 'Todos' : 'All'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
                <SelectItem value="pending">{t('pending')}</SelectItem>
                <SelectItem value="approved">{t('approved')}</SelectItem>
                <SelectItem value="rejected">{t('rejected')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">{t('employees')}</TableHead>
                <TableHead className="font-semibold">{t('start_date')}</TableHead>
                <TableHead className="font-semibold">{t('end_date')}</TableHead>
                <TableHead className="font-semibold">{t('days')}</TableHead>
                <TableHead className="font-semibold">{t('status')}</TableHead>
                <TableHead className="font-semibold">{t('notes')}</TableHead>
                <TableHead className="font-semibold text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vacations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    {t('no_results')}
                  </TableCell>
                </TableRow>
              ) : (
                vacations.map((vacation) => (
                  <TableRow key={vacation.id} className="hover:bg-slate-50" data-testid={`vacation-row-${vacation.id}`}>
                    <TableCell className="font-medium">{getEmployeeName(vacation.employee_id)}</TableCell>
                    <TableCell className="text-slate-600">{vacation.start_date}</TableCell>
                    <TableCell className="text-slate-600">{vacation.end_date}</TableCell>
                    <TableCell className="font-mono tabular-nums">{vacation.days}</TableCell>
                    <TableCell>{getStatusBadge(vacation.status)}</TableCell>
                    <TableCell className="text-slate-500 max-w-xs truncate">{vacation.notes || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {vacation.status === 'pending' && (isAdmin() || isSupervisor()) && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleApprove(vacation.id)}
                              data-testid={`approve-vacation-${vacation.id}`}
                              title={t('approve')}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleReject(vacation.id)}
                              data-testid={`reject-vacation-${vacation.id}`}
                              title={t('reject')}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {(isAdmin() || isSupervisor() || (isWorker() && vacation.status === 'pending')) && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEdit(vacation)}
                              data-testid={`edit-vacation-${vacation.id}`}
                              title={t('edit')}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(vacation.id)}
                              data-testid={`delete-vacation-${vacation.id}`}
                              title={t('delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Vacations;
